
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from "../config";
import { authHeaders } from "../utils/authHeaders";
import "../styles/procurement-admin.css";


const newAgentForm = () => ({ name: '', phone: '', password: '', sales_module_enabled: true, procurement_module_enabled: false });

function ModuleControls({ agent, onUpdate }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [unverified, setUnverified] = useState(false);
  const save = async (field, value) => {
    setSaving(true);
    setError('');
    const url = API_BASE + '/agents/' + agent.id + '/module-access';
    const headers = authHeaders({ 'Content-Type': 'application/json' });
    try {
      const res = await fetch(url, { method: 'PUT', headers, body: JSON.stringify({
        sales_module_enabled: agent.sales_module_enabled,
        procurement_module_enabled: agent.procurement_module_enabled, [field]: value,
      }) });
      const data = await res.json();
      if (!res.ok || !data.success || !data.agent) throw new Error(data.message || 'Module access save failed');
      onUpdate(agent.id, data.agent);
      setUnverified(false);
    } catch (err) {
      setError(err.message || 'Module access save failed');
      try {
        const res = await fetch(url, { headers });
        const data = await res.json();
        if (!res.ok || !data.success || !data.agent) throw new Error('Refresh failed');
        onUpdate(agent.id, data.agent);
        setUnverified(false);
      } catch (_) { setUnverified(true); }
    } finally { setSaving(false); }
  };
  return <div className="proc-admin-modules">
    {unverified ? <p role="alert">Current module access could not be verified. Reload the page before editing.</p> : <>
      <label>Sales Module <select aria-label={'Sales Module for ' + agent.name} disabled={saving}
        value={String(agent.sales_module_enabled)} onChange={e => save('sales_module_enabled', e.target.value === 'true')}>
        <option value="true">ON</option><option value="false">OFF</option>
      </select></label>{' '}
      <label>Procurement Module <select aria-label={'Procurement Module for ' + agent.name} disabled={saving}
        value={String(agent.procurement_module_enabled)} onChange={e => save('procurement_module_enabled', e.target.value === 'true')}>
        <option value="true">ON</option><option value="false">OFF</option>
      </select></label>
      <div>Procurement Linkage: {agent.procurement_linked ? 'Active' : 'No active linkage'}</div>
      <div>Effective Procurement Access: {agent.procurement_effective ? 'Yes' : 'No'}</div>
      {agent.procurement_module_enabled && !agent.procurement_linked && <p>Configured ON. A Procurement Aggregator relationship is also required. Use 'Link as Procurement Aggregator' for an eligible Agent.</p>}
    </>}
    {saving && <span role="status">Saving module access…</span>}
    {error && <p role="alert">{error}</p>}
  </div>;
}

function newLinkId() {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function ProcurementLink({ agent, reload }) {
  const pending = useRef(null);
  const running = useRef(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [unverified, setUnverified] = useState(false);
  const eligible = agent.procurement_link_state === 'UNLINKED' && agent.procurement_link_allowed === true;
  const link = async () => {
    if (running.current || !eligible || unverified) return;
    if (!window.confirm(`Link ${agent.name} as Procurement Aggregator using this Agent's existing login identity? No additional login or password will be created.`)) return;
    running.current = true;
    setBusy(true);
    setMessage('');
    try {
      if (!pending.current) pending.current = {
        command_id: newLinkId(), entity_id: newLinkId(), action: 'membership.create',
        data: { role: 'AGGREGATOR', agent_id: agent.id, name: agent.name },
      };
      const res = await fetch(API_BASE + '/procurement/v1/commands', {
        method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(pending.current),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Agent link failed');
      pending.current = null;
      const refreshed = await reload();
      setUnverified(!refreshed);
      setMessage(refreshed ? 'Agent linked as Procurement Aggregator.' :
        'Agent linked as Procurement Aggregator. The Agent list could not be refreshed; please refresh this page.');
    } catch (error) {
      setMessage(error.message || 'Agent link failed');
      setUnverified(!(await reload()));
    } finally {
      running.current = false;
      setBusy(false);
    }
  };
  const labels = {
    ACTIVE: 'Linked as Procurement Aggregator',
    UNLINKED: 'Not linked',
    INACTIVE_RELATIONSHIP: 'Procurement Aggregator relationship inactive',
    ACTOR_DISABLED: 'Procurement actor disabled',
  };
  return <div className="proc-admin-link">
    <div>{labels[agent.procurement_link_state] || 'Procurement linkage unavailable'}</div>
    {agent.procurement_link_state !== 'ACTIVE' && agent.procurement_link_block_reason && <div>{agent.procurement_link_block_reason}</div>}
    {eligible && !unverified && <button disabled={busy} onClick={link}>Link as Procurement Aggregator</button>}
    {unverified && <p role="alert">Current Procurement linkage could not be verified. Refresh this page before linking.</p>}
    {busy && <span role="status">Linking Agent…</span>}
    {message && <p role="status">{message}</p>}
  </div>;
}

function Agents() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [creating, setCreating] = useState(false);

  // Add Agent modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [form, setForm] = useState(newAgentForm());

  // Reset Password modal
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetAgent, setResetAgent] = useState(null); // {id,name,phone}
  const [newPassword, setNewPassword] = useState("");
  const [resetResult, setResetResult] = useState(""); // show returned password/msg

  // --------------------------
  // LOAD AGENTS
  // --------------------------
  const loadAgents = async () => {
    try {
      const res = await fetch(`${API_BASE}/agents`, {
       headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok || !Array.isArray(data)) throw new Error(data.message || 'Failed to load Agents');
      setAgents(data);
      setLoadError('');
      return true;
    } catch (err) {
      setLoadError(err.message || 'Failed to load Agents');
      return false;
    }
  };

  useEffect(() => {
    loadAgents();
  }, []);

  // --------------------------
  // ADD AGENT (POST only)
  // --------------------------
  const saveAgent = async () => {
    if (creating) return;
    if (!form.name || !form.phone || !form.password) {
      alert("Name, Phone and Password are required");
      return;
    }

    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/agents`, {
        method: "POST",
        headers: authHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          password: form.password,
          sales_module_enabled: form.sales_module_enabled,
          procurement_module_enabled: form.procurement_module_enabled,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data.message || "Failed to add agent");
        return;
      }

      setShowAddModal(false);
setForm(newAgentForm());
await loadAgents();

alert(
  "Agent created successfully. This agent currently has access to all FPO-mapped villages until villages are assigned."
);
    } catch (e) {
      console.error(e);
      alert("Network error while adding agent");
    } finally { setCreating(false); }
  };

  // --------------------------
  // OPEN RESET PASSWORD MODAL
  // --------------------------
  const openReset = (agent) => {
    setResetAgent(agent);
    setNewPassword("");
    setResetResult("");
    setShowResetModal(true);
  };

  // --------------------------
  // RESET PASSWORD (Admin)
  // Requires backend: POST /api/agents/:id/reset-password
  // Body: { new_password: "xxxx" }  (or backend can auto-generate if empty)
  // --------------------------
  const resetPassword = async () => {
    if (!resetAgent?.id) return;

    // You can enforce min length
    if (!newPassword || newPassword.length < 4) {
      alert("Enter a new password (min 4 chars)");
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/agents/${resetAgent.id}/reset-password`,
        {
          method: "POST",
          headers: authHeaders({
            "Content-Type": "application/json",
          }),
          body: JSON.stringify({ new_password: newPassword }),
        }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data.message || "Failed to reset password");
        return;
      }

      // If backend returns password or message, show it
      setResetResult(
        data.temp_password
          ? `Temporary password: ${data.temp_password}`
          : data.message || "Password reset successfully"
      );

      // Optional: close modal automatically after success
      // setShowResetModal(false);
    } catch (e) {
      console.error(e);
      alert("Network error while resetting password");
    }
  };

  return (
    <div className="proc-admin proc-admin--agents" style={{ padding: "20px" }}>
      <h2>Agents</h2>
      {loadError && <p role="alert">{loadError}</p>}

      <button
        style={{
          padding: "10px 20px",
          background: "#0066ff",
          color: "white",
          border: "none",
          borderRadius: "6px",
          marginBottom: "15px",
        }}
        onClick={() => {
          setForm(newAgentForm());
          setShowAddModal(true);
        }}
      >
        Add Agent
      </button>

      {/* ----------- TABLE ------------- */}
      <div className="proc-admin-table-scroll" role="region" aria-label="Agents table" tabIndex={0}>
      <table className="styled-table proc-admin-table">
        <thead>
          <tr style={{ background: "#ddd" }}>
  <th style={{ padding: "10px" }}>Name</th>
  <th style={{ padding: "10px" }}>Phone</th>
  <th style={{ padding: "10px" }}>Village Access</th>
  <th style={{ padding: "10px" }}>Assigned Villages</th>
  <th style={{ padding: "10px" }}>Field Modules</th>
  <th style={{ padding: "10px" }}>Actions</th>
</tr>
        </thead>

        <tbody>
          {agents.map((a) => (
  <tr key={a.id} style={{ borderBottom: "1px solid #ccc" }}>
    <td style={{ padding: "10px" }}>{a.name}</td>
<td style={{ padding: "10px" }}>{a.phone}</td>

<td style={{ padding: "10px" }}>
  {a.village_restriction_status === "RESTRICTED" ? (
    <span
      style={{
        padding: "4px 8px",
        borderRadius: 999,
        background: "#e8f7ee",
        color: "#146c2e",
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      Restricted
    </span>
  ) : (
    <span
      style={{
        padding: "4px 8px",
        borderRadius: 999,
        background: "#fff3cd",
        color: "#7a5200",
        fontWeight: 600,
        fontSize: 12,
      }}
    >
      Not Restricted
    </span>
  )}
</td>

<td style={{ padding: "10px" }}>
  {a.village_access_label ||
    (a.assigned_village_count > 0
      ? `${a.assigned_village_count} village(s)`
      : "All FPO-mapped villages")}
</td>

<td className="proc-admin-modules-cell" style={{ padding: "10px" }}><ModuleControls agent={a} onUpdate={(id, access) => setAgents(current => current.map(row => row.id === id ? { ...row, ...access } : row))} /></td>
<td className="proc-admin-actions-cell" style={{ padding: "10px" }}>
  <ProcurementLink agent={a} reload={loadAgents} />
  <div className="proc-admin-action-buttons">
    <button
      onClick={() => openReset(a)}
      style={{
        background: "#0d6efd",
        color: "#fff",
        border: "none",
        padding: "8px 10px",
        borderRadius: "6px",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      Reset Password
    </button>

    <button
      onClick={() => navigate(`/agent-geo-mapping/${a.id}`)}
      style={{
        background: "#198754",
        color: "#fff",
        border: "none",
        padding: "8px 10px",
        borderRadius: "6px",
        cursor: "pointer",
        whiteSpace: "nowrap",
      }}
    >
      Assign Villages
    </button>
  </div>
</td>
  </tr>
))}

          {agents.length === 0 && (
            <tr>
              <td colSpan="6" style={{ padding: "10px", color: "#777" }}>
                No agents found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {/* ----------- ADD MODAL ------------- */}
      {showAddModal && (
        <div
          className="proc-admin-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className="proc-admin-modal proc-admin-modal--add"
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <h3>Add Agent</h3>
            <label>Sales Module <select aria-label="Initial Sales Module" value={String(form.sales_module_enabled)} disabled={creating}
              onChange={e => setForm({ ...form, sales_module_enabled: e.target.value === 'true' })}>
              <option value="true">ON</option><option value="false">OFF</option>
            </select></label>
            <label>Procurement Module <select aria-label="Initial Procurement Module" value={String(form.procurement_module_enabled)} disabled={creating}
              onChange={e => setForm({ ...form, procurement_module_enabled: e.target.value === 'true' })}>
              <option value="true">ON</option><option value="false">OFF</option>
            </select></label>
            {form.procurement_module_enabled && <p>Configured Procurement: ON. No active linkage; effective access: No. After creation, use 'Link as Procurement Aggregator' for an eligible Agent.</p>}

            <input
              type="text"
              placeholder="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "10px",
                border: "1px solid #ccc",
              }}
            />

            <input
              type="text"
              placeholder="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "10px",
                border: "1px solid #ccc",
              }}
            />

            <input
              type="password"
              placeholder="Password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "10px",
                border: "1px solid #ccc",
              }}
            />

            <button
              disabled={creating}
              onClick={saveAgent}
              style={{
                background: "#0066ff",
                color: "white",
                padding: "10px",
                width: "100%",
                border: "none",
                borderRadius: "6px",
              }}
            >
              Save
            </button>

            <button
              onClick={() => setShowAddModal(false)}
              style={{
                background: "grey",
                color: "white",
                padding: "10px",
                marginTop: "10px",
                width: "100%",
                border: "none",
                borderRadius: "6px",
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ----------- RESET PASSWORD MODAL ------------- */}
      {showResetModal && resetAgent && (
        <div
          className="proc-admin-modal-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.3)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            className="proc-admin-modal proc-admin-modal--reset"
            style={{
              background: "white",
              padding: "20px",
              borderRadius: "8px",
            }}
          >
            <h3>Reset Password</h3>

            <div style={{ marginBottom: "10px", color: "#333" }}>
              Agent: <b>{resetAgent.name}</b> ({resetAgent.phone})
            </div>

            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: "100%",
                marginBottom: "10px",
                padding: "10px",
                border: "1px solid #ccc",
              }}
            />

            <button
              onClick={resetPassword}
              style={{
                background: "#444",
                color: "white",
                padding: "10px",
                width: "100%",
                border: "none",
                borderRadius: "6px",
              }}
            >
              Reset
            </button>

            {resetResult && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "10px",
                  background: "#f3f3f3",
                  borderRadius: "6px",
                  fontSize: "14px",
                }}
              >
                {resetResult}
              </div>
            )}

            <button
              onClick={() => setShowResetModal(false)}
              style={{
                background: "grey",
                color: "white",
                padding: "10px",
                marginTop: "10px",
                width: "100%",
                border: "none",
                borderRadius: "6px",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default Agents;
